import { useState } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, IconButton } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../theme/designTheme';

// ── Tab metadata ──────────────────────────────────────────────────────────────

const TAB_META = {
  'Redflag 01': {
    label: 'RED FLAG 01: ACCOUNT PROLIFERATION',
    title: 'Excessive Account Ownership Analysis',
    description:
      'Subject holds an anomalous number of accounts exceeding the defined threshold. Multi-account ownership at this scale is consistent with deliberate fragmentation of financial activity to obscure fund flows and reduce detection exposure across institutions.',
    alertLevel: 'CRITICAL',
    alertColor: colors.critical,
    alertBg: colors.criticalBg,
    status: 'REVIEWING',
    statusColor: colors.high,
  },
  'Redflag 02': {
    label: 'RED FLAG 02: LAYERING',
    title: 'U-Turn Transaction Analysis',
    description:
      'Detected multiple U-turn transactions where funds returned to origin via intermediary entities within 30 days. This pattern suggests layered money laundering or circular funding flows.',
    alertLevel: 'HIGH',
    alertColor: colors.high,
    alertBg: colors.highBg,
    status: 'OPEN',
    statusColor: colors.medium,
  },
  'Redflag 03': {
    label: 'RED FLAG 03: STRUCTURING',
    title: 'Sanctioned Country Exposure',
    description:
      'Transactions identified to or from sanctioned jurisdictions. Entities in flagged countries may indicate deliberate obfuscation of fund origins or compliance violations.',
    alertLevel: 'CRITICAL',
    alertColor: colors.critical,
    alertBg: colors.criticalBg,
    status: 'ESCALATED',
    statusColor: colors.critical,
  },
  'Redflag 04': {
    label: 'RED FLAG 04: BENEFICIAL OWNER',
    title: 'Beneficial Ownership Analysis',
    description:
      'Corporate structure obscures the true beneficial owner. Shell entities detected with no operational activity. Ownership chains exceed 3 layers of complexity, consistent with evasion patterns.',
    alertLevel: 'HIGH',
    alertColor: colors.high,
    alertBg: colors.highBg,
    status: 'OPEN',
    statusColor: colors.medium,
  },
};

// ── Column config per tab ─────────────────────────────────────────────────────

const tabColumns = {
  'Redflag 01': {
    template: '1fr 1fr',
    headers: ['CUSTOMER ID', 'NUM ACCOUNTS'],
    fields: ['customer_id', 'num_accounts'],
  },
  'Redflag 02': {
    template: '80px 1.3fr 1fr 1fr 1fr 1fr 90px 80px',
    headers: ['CUSTOMER ID', 'OUTBOUND COUNTRY', 'OUTBOUND DATE', 'AMOUNT (OUT)', 'INBOUND DATE', 'AMOUNT (IN)', 'DAYS BETWEEN', 'ALERT LEVEL'],
    fields: ['customer_id', 'outbound_country', 'outbound_date', 'outbound_amount', 'inbound_date', 'inbound_amount', 'days_between', '_alert'],
  },
  'Redflag 03': {
    template: '80px 1.2fr 1fr 1fr 1fr 1fr',
    headers: ['CUSTOMER ID', 'COUNTRY', '# TRANSACTIONS', 'TOTAL AMOUNT', 'FIRST TXN', 'LAST TXN'],
    fields: ['customer_id', 'outbound_country', 'num_of_transaction', 'total_txn_amount', 'first_txn_date', 'last_txn_date'],
  },
};
tabColumns['Redflag 04'] = tabColumns['Redflag 03'];

// ── Helpers ───────────────────────────────────────────────────────────────────

const COUNTRY_CODES = {
  India: 'IN', Brazil: 'BR', Singapore: 'SG', UAE: 'AE', Cyprus: 'CY',
  China: 'CN', 'United States': 'US', USA: 'US', 'United Kingdom': 'GB',
  UK: 'GB', Germany: 'DE', France: 'FR', Malaysia: 'MY', Indonesia: 'ID',
  Thailand: 'TH', Philippines: 'PH', Vietnam: 'VN', Australia: 'AU',
  Japan: 'JP', 'Hong Kong': 'HK', Russia: 'RU', Turkey: 'TR',
};

const getCountryCode = (name) =>
  COUNTRY_CODES[name] || (name ? name.slice(0, 2).toUpperCase() : '??');

const getAlertFromDays = (days) => {
  const d = parseInt(days, 10);
  if (d <= 4)  return { level: 'CRITICAL', color: colors.critical, bg: colors.criticalBg };
  if (d <= 14) return { level: 'HIGH',     color: colors.high,     bg: colors.highBg     };
  return              { level: 'MEDIUM',   color: colors.medium,   bg: colors.mediumBg   };
};

const fmt = (val) =>
  Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Cell renderer ─────────────────────────────────────────────────────────────

const renderCell = (field, row) => {
  const value = row[field];

  if (field === 'customer_id') {
    return (
      <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontWeight: 600, fontSize: '13px' }}>
        {value}
      </Typography>
    );
  }

  if (field === 'outbound_country') {
    const code = getCountryCode(value);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontSize: '12px' }}>
          {value}
        </Typography>
        <Box sx={{ bgcolor: colors.accentDim, border: `1px solid ${colors.border}`, borderRadius: '3px', px: 0.5, lineHeight: 1 }}>
          <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700 }}>
            {code}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (field === 'outbound_amount') {
    return (
      <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontWeight: 700, fontSize: '13px' }}>
        {fmt(value)}
      </Typography>
    );
  }

  if (field === 'inbound_amount' || field === 'total_txn_amount') {
    return (
      <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontSize: '12px' }}>
        {fmt(value)}
      </Typography>
    );
  }

  if (field === 'days_between') {
    const alert = getAlertFromDays(value);
    return (
      <Box sx={{ display: 'inline-flex', bgcolor: `${alert.color}18`, border: `1px solid ${alert.color}40`, borderRadius: '20px', px: 1.25, py: 0.3 }}>
        <Typography sx={{ color: alert.color, fontFamily: 'Lexend', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {value} Days
        </Typography>
      </Box>
    );
  }

  if (field === '_alert') {
    const alert = getAlertFromDays(row.days_between);
    return (
      <Box sx={{ display: 'inline-flex', bgcolor: alert.bg, border: `1px solid ${alert.color}50`, borderRadius: '4px', px: 1, py: 0.3 }}>
        <Typography sx={{ color: alert.color, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em' }}>
          {alert.level}
        </Typography>
      </Box>
    );
  }

  if (field === 'num_of_transaction') {
    return (
      <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontWeight: 600, fontSize: '13px' }}>
        {value}
      </Typography>
    );
  }

  // Default — dates, num_accounts, etc.
  return (
    <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '12px' }}>
      {value}
    </Typography>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 10;

const RedFlagsCard = ({ tabsData }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage]         = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const tabs    = Object.keys(tabsData);
  const current = tabs[tabIndex];
  const meta    = TAB_META[current] || {};
  const allRows = tabsData[current] || [];
  const colCfg  = tabColumns[current] || tabColumns['Redflag 03'];

  const totalPages  = Math.ceil(allRows.length / ROWS_PER_PAGE);
  const visibleRows = allRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const handleTabChange = (i) => { setTabIndex(i); setPage(0); setDialogOpen(false); };
  const handleOpenDialog  = () => { setPage(0); setDialogOpen(true); };
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Tab bar ──────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', borderBottom: `1px solid ${colors.border}`,
        overflowX: 'auto', flexShrink: 0,
        '&::-webkit-scrollbar': { height: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: colors.border, borderRadius: 2 },
      }}>
        {tabs.map((tab, i) => {
          const isActive = i === tabIndex;
          const m = TAB_META[tab] || {};
          return (
            <Box
              key={tab}
              onClick={() => handleTabChange(i)}
              sx={{
                px: 2.5, py: 1.5, cursor: 'pointer', flexShrink: 0,
                borderBottom: isActive ? `2px solid ${colors.accent}` : '2px solid transparent',
                bgcolor: isActive ? colors.accentDim : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: colors.surfaceElevated },
              }}
            >
              <Typography sx={{ fontFamily: 'Lexend', fontSize: '10px', fontWeight: isActive ? 700 : 500, letterSpacing: '0.08em', color: isActive ? colors.accent : colors.textMuted, whiteSpace: 'nowrap' }}>
                {m.label || tab}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Overview card ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 2, px: 2.5, pt: 2, pb: 2, flexShrink: 0, alignItems: 'flex-start' }}>

          {/* Left: title + description */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', mb: 0.5 }}>
              ACTIVE INVESTIGATION
            </Typography>
            <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 800, fontSize: '17px', textTransform: 'uppercase', lineHeight: 1.2, mb: 1 }}>
              {meta.title}
            </Typography>
            <Box sx={{ borderLeft: `3px solid ${colors.accent}`, pl: 1.5 }}>
              <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px', lineHeight: 1.6 }}>
                {meta.description}
              </Typography>
            </Box>
          </Box>

          {/* Right: detections card */}
          <Box sx={{
            flexShrink: 0, bgcolor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`, borderRadius: 2,
            p: 1.5, minWidth: 155, textAlign: 'center',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
              <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.12em' }}>
                DETECTIONS
              </Typography>
              <WarningAmberIcon sx={{ color: colors.critical, fontSize: 14 }} />
            </Box>
            <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 800, fontSize: '38px', lineHeight: 1 }}>
              {allRows.length}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 600, letterSpacing: '0.1em', mb: 1 }}>
              ANOMALOUS CLUSTERS
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', mb: 1.25 }}>
              <Box sx={{ bgcolor: meta.alertBg, border: `1px solid ${meta.alertColor}50`, borderRadius: '4px', px: 1, py: 0.35 }}>
                <Typography sx={{ color: meta.alertColor, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em' }}>
                  {meta.alertLevel}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: colors.accentDim, border: `1px solid ${colors.border}`, borderRadius: '4px', px: 1, py: 0.35 }}>
                <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em' }}>
                  {meta.status}
                </Typography>
              </Box>
            </Box>
            {/* SEE DATA button */}
            <Box
              onClick={allRows.length > 0 ? handleOpenDialog : undefined}
              sx={{
                bgcolor: colors.accentDim,
                border: `1px solid ${colors.accent}40`,
                borderRadius: '6px',
                py: 0.6,
                cursor: allRows.length > 0 ? 'pointer' : 'default',
                opacity: allRows.length > 0 ? 1 : 0.4,
                transition: 'all 0.15s',
                '&:hover': allRows.length > 0 ? { bgcolor: `${colors.accent}22`, borderColor: colors.accent } : {},
              }}
            >
              <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em' }}>
                SEE DATA
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Data popup ────────────────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            backgroundImage: 'none',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Dialog header: flag info */}
          <Box sx={{
            px: 3, pt: 2.5, pb: 2,
            borderBottom: `1px solid ${colors.border}`,
            flexShrink: 0,
            bgcolor: colors.surfaceElevated,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', mb: 0.5 }}>
                  {meta.label}
                </Typography>
                <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 800, fontSize: '18px', textTransform: 'uppercase', lineHeight: 1.2, mb: 1 }}>
                  {meta.title}
                </Typography>
                <Box sx={{ borderLeft: `3px solid ${colors.accent}`, pl: 1.5 }}>
                  <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px', lineHeight: 1.6 }}>
                    {meta.description}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseDialog} size="small" sx={{ color: colors.textMuted, flexShrink: 0, mt: -0.5, '&:hover': { color: colors.textPrimary } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Live feed bar */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 3, py: 0.9,
            bgcolor: colors.surfaceElevated,
            borderBottom: `1px solid ${colors.borderSubtle}`,
            flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em' }}>
                LIVE FEED: {meta.label}
              </Typography>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.accent, boxShadow: `0 0 6px ${colors.accent}` }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {['FILTER', 'EXPORT'].map((label) => (
                <Button key={label} size="small" sx={{
                  color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700,
                  letterSpacing: '0.08em', border: `1px solid ${colors.border}`, borderRadius: 1,
                  px: 1.25, py: 0.4, minWidth: 0, textTransform: 'none',
                  '&:hover': { borderColor: colors.accent, color: colors.accent, bgcolor: colors.accentDim },
                }}>
                  {label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Column headers */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: colCfg.template,
            px: 3, py: 1,
            borderBottom: `1px solid ${colors.border}`,
            flexShrink: 0,
          }}>
            {colCfg.headers.map((h) => (
              <Typography key={h} sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {/* Rows — scrollable */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {visibleRows.length > 0 ? visibleRows.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: colCfg.template,
                  alignItems: 'center',
                  px: 3, py: 1.25,
                  borderBottom: i < visibleRows.length - 1 ? `1px solid ${colors.borderSubtle}` : 'none',
                  transition: 'background 0.12s',
                  '&:hover': { bgcolor: colors.surfaceHover },
                }}
              >
                {colCfg.fields.map((field) => (
                  <Box key={field}>{renderCell(field, row)}</Box>
                ))}
              </Box>
            )) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '12px' }}>
                  No data for this flag type.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer / pagination */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 3, py: 1.25,
            borderTop: `1px solid ${colors.border}`,
            flexShrink: 0,
            bgcolor: colors.surfaceElevated,
          }}>
            <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Showing {page * ROWS_PER_PAGE + visibleRows.length} of {allRows.length} detected clusters
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {['Previous', 'Next'].map((label) => {
                const disabled = label === 'Previous' ? page === 0 : page >= totalPages - 1;
                return (
                  <Typography
                    key={label}
                    onClick={() => !disabled && setPage(p => label === 'Previous' ? p - 1 : p + 1)}
                    sx={{
                      fontFamily: 'Lexend', fontSize: '10px', fontWeight: 600,
                      color: disabled ? colors.textMuted : colors.textSecondary,
                      cursor: disabled ? 'default' : 'pointer',
                      '&:hover': { color: disabled ? colors.textMuted : colors.accent },
                    }}
                  >
                    {label}
                  </Typography>
                );
              })}
            </Box>
          </Box>

        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RedFlagsCard;
